import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Icon,
    IconButton,
    CircularProgress,
  } from '@mui/material';
  import React, {
    cloneElement,
    forwardRef,
    isValidElement,
    useImperativeHandle,
    useRef,
    useState,
    useCallback,
    useMemo,
  } from 'react';
  import { FormProvider, useForm } from 'react-hook-form';
  import { HookFormDialogRef, IHookFormDialogOptions } from '.';
  import {
    EditNodeTemplate,
    IEditNodeTemplateOptions,
  } from '@/components/edit-node-template';
  import { useTheme, Theme } from '@mui/material/styles';
  interface IStyleProps {
    minHeight: string;
    py: 0 | 1 | 2 | 3 | 4 | 5;
    px: 0 | 1 | 2 | 3 | 4 | 5;
  }
  
  interface IProps extends Partial<IStyleProps> {
    maxWidth?: 'md' | 'xs' | 'sm' | 'lg' | 'xl';
    fullWidth?: boolean;
  }
  
  const DEFAULT_OPTIONS = {
    title: 'Edit',
    content: 'React Hook Form dialog.',
    submitLabel: 'Save',
    cancelLabel: 'Cancel',
  };
  
  const useStyles = (props: IStyleProps, theme: Theme) => {
    const { minHeight, py, px } = props;
  
    return useMemo(
      () => ({
        dialog: {
          minHeight,
          paddingTop: theme.spacing(py),
          paddingBottom: theme.spacing(py),
          paddingRight: theme.spacing(px),
          paddingLeft: theme.spacing(px),
          '& .MuiTextField-root': {
            marginBottom: theme.spacing(2),
          },
          margin: 0,
          position: 'absolute',
          left: theme.spacing(1),
          top: '50%',
          transform: 'translateY(-50%)',
        },
        scrollPaper: {
          alignItems: 'center',
          justifyContent: 'flex-start',
        },
        buttons: {
          display: 'flex',
          alignItems: 'center',
          width: '100%',
        },
        btnStyle: {
          color: 'rgba(0, 0, 0, 0.87) !important', 
          '&:hover': {
            backgroundColor: '#d5d5d5 !important',
          },
        },
      }),
      [minHeight, py, px, theme]
    );
  };
  
  export const HookFormDialog = forwardRef<HookFormDialogRef, IProps>(
    (
      { minHeight = '600px', maxWidth = 'sm', fullWidth = true, py = 0, px = 0 },
      ref
    ) => {
      const theme = useTheme();
      const classes = useStyles({ minHeight, py, px }, theme);
  
      const [isOpen, setIsOpen] = useState<boolean>(false);
      const [fullscreen, setFullscreen] = useState<boolean>(false);
      const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
      const [submitError, setSubmitError] = useState<string | null>(null);
      const isClosingRef = useRef<boolean>(false);
  
      const templateRef = useRef<{
        open: (initOptions: IEditNodeTemplateOptions) => void;
      } | null>(null);
  
      const [options, setOptions] = useState<IHookFormDialogOptions>(
        DEFAULT_OPTIONS
      );
  
      useImperativeHandle(ref, () => ({
        open: (providedOptions: IHookFormDialogOptions) => {
          // Prevent opening if already open, submitting, or closing
          if (isOpen || isSubmitting || isClosingRef.current) {
            console.warn(
              'Dialog is already open, submitting, or closing, ignoring open request'
            );
            return;
          }
  
          setOptions({ ...DEFAULT_OPTIONS, ...providedOptions });
          setIsOpen(true);
          setSubmitError(null);
          setIsSubmitting(false);
          isClosingRef.current = false;
          
          // Set default values if provided
          if (providedOptions.defaultValues) {
            reset(providedOptions.defaultValues);
          }
        },
      }));
  
      const formMethods = useForm();
  
      const { reset, getValues } = formMethods;
  
      const cleanUp = useCallback(() => {
        // Set closing flag to prevent reopening
        isClosingRef.current = true;
  
        // First, close the dialog to prevent reopening
        setIsOpen(false);
        setIsSubmitting(false);
        setSubmitError(null);
  
        // Store the finishedAction before resetting options
        const finishedAction = options.finishedAction;
  
        // Reset all state
        setOptions(DEFAULT_OPTIONS);
        reset();
  
        // Call finishedAction last to prevent state conflicts
        if (typeof finishedAction === 'function') {
          // Use setTimeout to ensure state updates are complete
          setTimeout(() => {
            finishedAction();
            // Reset closing flag after finishedAction completes and a delay
            // Adding a longer delay to prevent race conditions
            setTimeout(() => {
              isClosingRef.current = false;
            }, 300);
          }, 0);
        } else {
          // Reset closing flag after a delay to prevent race conditions
          setTimeout(() => {
            isClosingRef.current = false;
          }, 300);
        }
      }, [options.finishedAction, reset]);
  
      const handleSubmit = useCallback(async () => {
        if (typeof options.submitAction === 'function') {
          setIsSubmitting(true);
          setSubmitError(null);
  
          try {
            const result = options.submitAction(getValues());
  
            // Handle both sync and async submitAction
            if (
              result &&
              typeof result === 'object' &&
              result !== null &&
              'then' in result &&
              typeof (result as any).then === 'function'
            ) {
              // Async function - await the result
              await result;
            }
            // Sync function - result is already processed
  
            cleanUp(); // Only close dialog on successful submission
          } catch (error) {
            console.error('Form submission failed:', error);
            setSubmitError(
              error instanceof Error ? error.message : 'Form submission failed'
            );
          } finally {
            setIsSubmitting(false);
          }
        } else {
          cleanUp();
        }
      }, [options.submitAction, getValues, cleanUp]);
  
      const openSaveAsTemplate = useCallback(() => {
        if (templateRef.current) {
          templateRef.current.open({
            saveAsTemplate: options.saveAsTemplate,
          });
        }
      }, [options.saveAsTemplate]);
  
      const handleDuplicate = useCallback(() => {
        if (typeof options.duplicate === 'function') {
          options.duplicate(getValues());
        }
      }, [options.duplicate, getValues]);
  
      const toggleFullscreen = useCallback(() => {
        setFullscreen((prev) => !prev);
      }, []);
  
      return (
        <Dialog
          open={isOpen}
          onClose={cleanUp}
          maxWidth={fullscreen ? 'xl' : maxWidth}
          fullWidth={fullWidth}
          slotProps={{
            paper: {
              sx: classes.dialog, 
            },
            backdrop: {
              sx: classes.scrollPaper,
            },
          }}
          aria-labelledby="dialog-title"
          aria-describedby="dialog-description"
        >
          <FormProvider {...formMethods}>
            <DialogTitle id="dialog-title">
              {options.title}
              {maxWidth === 'sm' && (
                <IconButton
                  aria-label={fullscreen ? 'minimize dialog' : 'maximize dialog'}
                  onClick={toggleFullscreen}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    opacity: 0.5,
                  }}
                >
                  <Icon>{fullscreen ? 'minimize' : 'open_in_full'}</Icon>
                </IconButton>
              )}
            </DialogTitle>
            <DialogContent id="dialog-description">
              {submitError && (
                <Box
                  sx={{
                    color: 'error.main',
                    mb: 2,
                    p: 1,
                    backgroundColor: 'error.light',
                    borderRadius: 1,
                  }}
                >
                  {submitError}
                </Box>
              )}
              {isValidElement(options.content)
                ? cloneElement(options.content)
                : options.content}
            </DialogContent>
            <DialogActions>
              <Box sx={classes.buttons}>
                <Box>
                  <Button
                    onClick={handleDuplicate}
                    startIcon={<Icon>content_copy</Icon>}
                    color="secondary"
                    disabled={isSubmitting}
                    aria-label="Duplicate this item"
                  >
                    Duplicate
                  </Button>
                  <Button
                    onClick={openSaveAsTemplate}
                    startIcon={<Icon>bookmark_add</Icon>}
                    color="secondary"
                    disabled={isSubmitting}
                    aria-label="Save as template"
                  >
                    Template
                  </Button>
                </Box>
                <Box sx={{ textAlign: 'right', flexGrow: 1 }}>
                  <Button
                    onClick={cleanUp}
                    disabled={isSubmitting}
                    aria-label="Cancel and close dialog"
                    sx={classes.btnStyle}
                  >
                    {options.cancelLabel}
                  </Button>
                  <Button
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    startIcon={
                      isSubmitting ? <CircularProgress size={16} /> : null
                    }
                    aria-label="Submit form"
                  >
                    {isSubmitting ? 'Saving...' : options.submitLabel}
                  </Button>
                </Box>
              </Box>
            </DialogActions>
          </FormProvider>
          <EditNodeTemplate ref={templateRef} />
        </Dialog>
      );
    }
  );